import { genKey, type RawDraftContentState } from 'draft-js';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isRange(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    typeof value.offset === 'number' &&
    Number.isInteger(value.offset) &&
    value.offset >= 0 &&
    typeof value.length === 'number' &&
    Number.isInteger(value.length) &&
    value.length >= 0
  );
}

function isRawDraftContentState(value: unknown): value is RawDraftContentState {
  if (!isRecord(value) || !Array.isArray(value.blocks) || !isRecord(value.entityMap)) {
    return false;
  }

  return (
    value.blocks.every(
      (block: unknown) =>
        isRecord(block) &&
        typeof block.key === 'string' &&
        typeof block.type === 'string' &&
        typeof block.text === 'string' &&
        typeof block.depth === 'number' &&
        Number.isInteger(block.depth) &&
        block.depth >= 0 &&
        Array.isArray(block.inlineStyleRanges) &&
        block.inlineStyleRanges.every(
          (range: unknown) => isRange(range) && typeof range.style === 'string'
        ) &&
        Array.isArray(block.entityRanges) &&
        block.entityRanges.every(
          (range: unknown) =>
            isRange(range) && typeof range.key === 'number' && Number.isInteger(range.key)
        ) &&
        (block.data === undefined || isRecord(block.data))
    ) &&
    Object.values(value.entityMap).every(
      (entity) =>
        isRecord(entity) &&
        typeof entity.type === 'string' &&
        (entity.mutability === 'MUTABLE' ||
          entity.mutability === 'IMMUTABLE' ||
          entity.mutability === 'SEGMENTED') &&
        isRecord(entity.data)
    )
  );
}

/** Validate legacy serialized notes before passing them into DraftJS. */
export function parseRawDraftContentState(value: string): RawDraftContentState | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed) || !Array.isArray(parsed.blocks) || !isRecord(parsed.entityMap)) return;
    // DraftJS supplies these defaults for older sparse blocks. Normalize them
    // before validation so strict typing preserves legacy note rendering.
    const normalized = {
      ...parsed,
      blocks: parsed.blocks.map((block: unknown) =>
        isRecord(block)
          ? {
              ...block,
              key: block.key || genKey(),
              type: block.type || 'unstyled',
              depth: block.depth || 0,
              data: block.data || {},
              inlineStyleRanges: block.inlineStyleRanges || [],
              entityRanges: block.entityRanges || [],
            }
          : block
      ),
      entityMap: Object.fromEntries(
        Object.entries(parsed.entityMap).map(([key, entity]) => [
          key,
          isRecord(entity) ? { ...entity, data: entity.data || {} } : entity,
        ])
      ),
    };
    return isRawDraftContentState(normalized) ? normalized : undefined;
  } catch {
    return undefined;
  }
}
